import React, { useMemo, useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HelpCircle, ChevronLeft, ChevronRight, User as UserIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { AdminUser } from "@/hooks/admin/useAdminUsers";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface UsersTableProps {
  users: AdminUser[];
  allIdsOnPage: string[];
  totalCount: number;
  isLoading: boolean;
  page: number;
  pageCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (n: number) => void;
  isSelected: (id: string) => boolean;
  toggleItem: (id: string) => void;
  toggleAll: () => void;
  onRowClick?: (user: AdminUser) => void;
}

export function UsersTable({
  users,
  allIdsOnPage,
  totalCount,
  isLoading,
  page,
  pageCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  isSelected,
  toggleItem,
  toggleAll,
  onRowClick,
}: UsersTableProps) {
  const [pageInputValue, setPageInputValue] = useState<string>(String(page));

  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  const allSelected = useMemo(() => allIdsOnPage.length > 0 && allIdsOnPage.every((id) => isSelected(id)), [allIdsOnPage, isSelected]);

  const headerCell = "text-left bg-muted/60 sticky top-0 border-b z-10 text-muted-foreground text-xs font-normal h-8 px-3 py-1";
  const bodyCell = "px-3 py-1.5 align-middle text-xs";

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border relative">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className={`${headerCell} w-[44px] min-w-[44px]`}>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => toggleAll()}
                  aria-label="Select all on page"
                />
              </TableHead>
              <TableHead className={`${headerCell} w-[44px] min-w-[44px]`}></TableHead>
              <TableHead className={`${headerCell} w-[32%]`}>Name</TableHead>
              <TableHead className={`${headerCell} w-[44%]`}>Email</TableHead>
              <TableHead className={`${headerCell} w-[16%]`}>Role</TableHead>
              <TableHead className={`${headerCell} w-[8%]`}>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users || []).map((u) => (
              <TableRow key={u.id} onClick={() => onRowClick?.(u)} className="cursor-pointer hover:bg-muted/40">
                <TableCell className={`${bodyCell} w-[44px] min-w-[44px]`} onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={!!isSelected(u.id)} onCheckedChange={() => toggleItem(u.id)} />
                </TableCell>
                <TableCell className={`${bodyCell} w-[44px] min-w-[44px]`} onClick={(e) => e.stopPropagation()}>
                  <Avatar className="h-8 w-8 rounded-full">
                    {u.avatar_url ? (
                      <AvatarImage
                        src={u.avatar_url}
                        alt={u.name || u.email || ""}
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        onError={(e: any) => { try { e.currentTarget.style.display = 'none' } catch {} }}
                      />
                    ) : null}
                    <AvatarFallback delayMs={250} className="rounded-full p-0 bg-muted text-muted-foreground">
                      <UserIcon className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                </TableCell>
                <TableCell className={`${bodyCell} truncate`}>{u.name || "-"}</TableCell>
                <TableCell className={`${bodyCell} truncate`}>{u.email || "-"}</TableCell>
                <TableCell className={`${bodyCell}`}>{u.role || "-"}</TableCell>
                <TableCell className={`${bodyCell}`}>
                  <span className="inline-flex items-center rounded border px-2 py-0.5 text-xs capitalize bg-muted/30">
                    {u.status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {(!users || users.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {isLoading ? "Loading users…" : "No users to display"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="border-t p-2 flex items-center justify-between bg-muted/20">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={isLoading || page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

        <div className="flex items-center text-sm">
            <span className="mr-2">Page</span>
            <Input
              className="h-8 w-[60px] text-center"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newPage = Number(pageInputValue);
                  if (!isNaN(newPage) && newPage >= 1 && newPage <= pageCount) {
                    onPageChange(newPage);
                  } else {
                    setPageInputValue(String(page));
                  }
                }
              }}
              onBlur={() => {
                const newPage = Number(pageInputValue);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= pageCount) {
                  onPageChange(newPage);
                } else {
                  setPageInputValue(String(page));
                }
              }}
              aria-label="Go to page"
              disabled={isLoading}
            />
            <span className="mx-2">of {pageCount}</span>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(Math.min(pageCount, page + 1))}
            disabled={isLoading || page === pageCount}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <div className="flex items-center space-x-4 ml-2">
            <Select value={String(rowsPerPage)} onValueChange={(v) => onRowsPerPageChange(Number(v))}>
              <SelectTrigger className="h-8 w-[120px]">
                <SelectValue placeholder={`${rowsPerPage} rows`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
                <SelectItem value="100">100 rows</SelectItem>
                <SelectItem value="500">500 rows</SelectItem>
                <SelectItem value="1000">1000 rows</SelectItem>
              </SelectContent>
            </Select>

            <div className="text-sm text-muted-foreground flex items-center">
              <span>{totalCount.toLocaleString()} records</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 ml-1">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total number of users matching filters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersTable;
